import type { SubscriptionSource, SubscriptionType } from "./enums";
import { matchesQuery, type LegislationSummary } from "./legislation";

/**
 * Subscription & entitlement logic — the heart of the rebuild.
 *
 * Rules (decided 2026-06-01):
 *  - Subscriptions live on the COMPANY only; users inherit. There are no
 *    per-user subscriptions to keep in sync.
 *  - "premium" subscriptions deliver richer / more alerts for a law. A company
 *    can only hold premium subscriptions if its billing plan is the premium
 *    tier; otherwise a premium request is granted as standard.
 *  - NO cascade: a subsidiary does not inherit a parent company's
 *    subscriptions. Each company stands alone.
 */

export type Subscription = {
  readonly legislationId: string;
  readonly subscriptionType: SubscriptionType;
  readonly source: SubscriptionSource;
};

export type SubscriptionLegislationIdRow<LegislationId extends string = string> = {
  readonly legislationId: LegislationId;
};

export type CoverageLegislationRow<LegislationId extends string = string> = LegislationSummary & {
  readonly _id: LegislationId;
};

export type AvailableCoverageLegislationInput<LegislationId extends string, Row extends CoverageLegislationRow<LegislationId>> = {
  readonly legislation: readonly Row[];
  readonly subscribedIds: ReadonlySet<LegislationId>;
  readonly query: string;
};

export const subscriptionLegislationIds = <LegislationId extends string>(
  subscriptions: readonly SubscriptionLegislationIdRow<LegislationId>[],
): ReadonlySet<LegislationId> => new Set(subscriptions.map((subscription) => subscription.legislationId));

export const availableCoverageLegislation = <LegislationId extends string, Row extends CoverageLegislationRow<LegislationId>>({
  legislation,
  subscribedIds,
  query,
}: AvailableCoverageLegislationInput<LegislationId, Row>): Row[] =>
  [...legislation]
    .filter((law) => !subscribedIds.has(law._id))
    .filter((law) => matchesQuery(law, query))
    .sort((a, b) => a.jurisdiction.localeCompare(b.jurisdiction) || a.title.localeCompare(b.title));

export type CoverageCatalogueView = {
  readonly availableCountLabel: string;
  readonly subscribedCountLabel: string;
  readonly emptyMessage: string;
};

const pluralLabel = (count: number, singular: string, plural: string): string =>
  `${count} ${count === 1 ? singular : plural}`;

export const coverageCatalogueView = ({
  availableCount,
  subscribedCount,
  query,
}: {
  readonly availableCount: number;
  readonly subscribedCount: number;
  readonly query: string;
}): CoverageCatalogueView => ({
  availableCountLabel: `${pluralLabel(availableCount, "available law", "available laws")}`,
  subscribedCountLabel: `${subscribedCount} subscribed`,
  emptyMessage: query.trim() === ""
    ? "All active laws are already subscribed."
    : "No available laws match this filter.",
});

export type CoverageBundleView =
  | {
      readonly kind: "subscribed";
      readonly owned: true;
      readonly instrumentCountLabel: string;
    }
  | {
      readonly kind: "available";
      readonly owned: false;
      readonly instrumentCountLabel: string;
      readonly addDisabled: boolean;
    };

export type CoverageBundleActionView =
  | {
      readonly kind: "subscribed";
      readonly action: "none";
      readonly variant: "soft";
      readonly icon: "check";
      readonly label: "Subscribed";
      readonly disabled: false;
    }
  | {
      readonly kind: "available";
      readonly action: "applyBundle";
      readonly variant: "outline";
      readonly icon: "plus";
      readonly label: "Add bundle";
      readonly disabled: boolean;
    };

export const coverageBundleView = <LegislationId extends string>({
  legislationIds,
  subscribedIds,
  selfServe,
}: {
  readonly legislationIds: readonly LegislationId[];
  readonly subscribedIds: ReadonlySet<LegislationId>;
  readonly selfServe: boolean;
}): CoverageBundleView => {
  const owned = legislationIds.length > 0 && legislationIds.every((legislationId) => subscribedIds.has(legislationId));
  const instrumentCountLabel = `${legislationIds.length} ${legislationIds.length === 1 ? "instrument" : "instruments"}`;
  return owned
    ? { kind: "subscribed", owned: true, instrumentCountLabel }
    : { kind: "available", owned: false, instrumentCountLabel, addDisabled: !selfServe };
};

export const coverageBundleActionView = (view: CoverageBundleView): CoverageBundleActionView => {
  switch (view.kind) {
    case "subscribed":
      return {
        kind: "subscribed",
        action: "none",
        variant: "soft",
        icon: "check",
        label: "Subscribed",
        disabled: false,
      };
    case "available":
      return {
        kind: "available",
        action: "applyBundle",
        variant: "outline",
        icon: "plus",
        label: "Add bundle",
        disabled: view.addDisabled,
      };
  }
};

export type Plan = {
  readonly tier: SubscriptionType;
};

export type CompanySubscriptionPatch = {
  readonly subscriptionType: SubscriptionType;
  readonly source: SubscriptionSource;
};

export type CompanySubscriptionInsert<CompanyId extends string = string, LegislationId extends string = string> =
  CompanySubscriptionPatch & {
    readonly companyId: CompanyId;
    readonly legislationId: LegislationId;
    readonly addedAt: number;
  };

export type CompanySubscriptionInsertInput<CompanyId extends string = string, LegislationId extends string = string> = {
  readonly companyId: CompanyId;
  readonly legislationId: LegislationId;
  readonly subscriptionType: SubscriptionType;
  readonly source: SubscriptionSource;
  readonly now: number;
};

export const companySubscriptionPatch = (
  subscriptionType: SubscriptionType,
  source: SubscriptionSource,
): CompanySubscriptionPatch => ({ subscriptionType, source });

export const companySubscriptionInsert = <CompanyId extends string, LegislationId extends string>(
  input: CompanySubscriptionInsertInput<CompanyId, LegislationId>,
): CompanySubscriptionInsert<CompanyId, LegislationId> => ({
  companyId: input.companyId,
  legislationId: input.legislationId,
  subscriptionType: input.subscriptionType,
  source: input.source,
  addedAt: input.now,
});

/** The alert tier a company is entitled to, derived from its active plan. */
export const companyTier = (plan: Plan | null): SubscriptionType =>
  plan?.tier === "premium" ? "premium" : "standard";

export const canUsePremiumAlerts = (plan: Plan | null): boolean =>
  companyTier(plan) === "premium";

export type PremiumCoverageToggleView = {
  readonly disabled: boolean;
  readonly checked: boolean;
  readonly labelToneClass: string;
  readonly title: string;
  readonly requestedType: SubscriptionType;
};

export const premiumCoverageToggleView = ({
  selfServe,
  premiumEntitled,
  asPremium,
}: {
  readonly selfServe: boolean;
  readonly premiumEntitled: boolean;
  readonly asPremium: boolean;
}): PremiumCoverageToggleView => {
  const premiumRequested = asPremium && premiumEntitled;
  return {
    disabled: !selfServe || !premiumEntitled,
    checked: premiumRequested,
    labelToneClass: premiumEntitled ? "text-slate-600" : "text-slate-400",
    title: premiumEntitled ? "" : "Requires a premium plan",
    requestedType: premiumRequested ? "premium" : "standard",
  };
};

/**
 * The subscription type actually granted: a premium request is downgraded to
 * standard unless the company's plan unlocks premium.
 */
export const grantedType = (
  requested: SubscriptionType,
  plan: Plan | null,
): SubscriptionType =>
  requested === "premium" && canUsePremiumAlerts(plan) ? "premium" : "standard";

/** Expand a bundle into subscriptions at the company's entitled tier. */
export const subscriptionsFromBundle = (
  legislationIds: readonly string[],
  plan: Plan | null,
): Subscription[] =>
  legislationIds.map((legislationId) => ({
    legislationId,
    subscriptionType: companyTier(plan),
    source: "bundle" as const,
  }));

/**
 * Merge subscription lists, de-duplicating by legislation. On conflict:
 *  - a `manual` subscription wins over a `bundle` one (explicit user choice);
 *  - otherwise the higher tier (premium > standard) wins.
 */
export const mergeSubscriptions = (
  ...lists: ReadonlyArray<readonly Subscription[]>
): Subscription[] => {
  const byLeg = new Map<string, Subscription>();
  for (const list of lists) {
    for (const sub of list) {
      const existing = byLeg.get(sub.legislationId);
      byLeg.set(sub.legislationId, existing ? pickWinner(existing, sub) : sub);
    }
  }
  return [...byLeg.values()];
};

const pickWinner = (a: Subscription, b: Subscription): Subscription => {
  if (a.source !== b.source) return a.source === "manual" ? a : b;
  return a.subscriptionType === "premium" ? a : b;
};

export const isSubscribed = (
  subs: readonly Subscription[],
  legislationId: string,
): boolean => subs.some((s) => s.legislationId === legislationId);

/**
 * Resolve a company's *effective* subscriptions. Intentionally a pass-through
 * that does NOT fold in a parent company's subscriptions — encoding the
 * no-cascade rule explicitly so the boundary is testable.
 */
export const effectiveSubscriptions = (
  ownSubscriptions: readonly Subscription[],
): Subscription[] => [...ownSubscriptions];
